import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { MapPin, Package, DollarSign, MessageSquare, ArrowLeft, FileCheck, Shield, AlertTriangle, FileText, CheckCircle, XCircle, Clock, Lock } from 'lucide-react';
import { toast } from 'sonner';

import { API_URL } from '@/config/api';
import { tCategory } from '@/i18n';

// Incoterms 2020
const INCOTERMS = [
  { code: "EXW", name: "Ex Works" },
  { code: "FCA", name: "Free Carrier" },
  { code: "CPT", name: "Carriage Paid To" },
  { code: "CIP", name: "Carriage and Insurance Paid To" },
  { code: "DAP", name: "Delivered at Place" },
  { code: "DPU", name: "Delivered at Place Unloaded" },
  { code: "DDP", name: "Delivered Duty Paid" },
  { code: "FAS", name: "Free Alongside Ship" },
  { code: "FOB", name: "Free on Board" },
  { code: "CFR", name: "Cost and Freight" },
  { code: "CIF", name: "Cost, Insurance and Freight" },
];

export default function MineralDetail() {
  const { t } = useTranslation();
  const [mineral, setMineral] = useState(null);
  const [loading, setLoading] = useState(true);
  const [offerDialogOpen, setOfferDialogOpen] = useState(false);
  const [ndaDialogOpen, setNdaDialogOpen] = useState(false);
  const [ndaAccepted, setNdaAccepted] = useState(false);
  const [ndaCheckbox, setNdaCheckbox] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [offerData, setOfferData] = useState({
    offer_price: '',
    quantity: '',
    message: '',
    incoterm: 'EXW'
  });
  const { id } = useParams();
  const { user, getAuthHeader } = useAuth();
  const navigate = useNavigate();

  const fetchMineral = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/minerals/${id}`);
      setMineral(response.data);
      // Set default incoterm from mineral
      if (response.data.incoterm) {
        setOfferData(prev => ({ ...prev, incoterm: response.data.incoterm }));
      }
    } catch (error) {
      toast.error('Failed to fetch mineral details');
      navigate('/');
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchDocuments = useCallback(async () => {
    setDocumentsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/documents/mineral/${id}`, {
        headers: getAuthHeader()
      });
      setDocuments(response.data);
    } catch (error) {
      if (error.response?.status === 403) {
        // NDA not accepted - this is expected
      } else {
        console.error('Failed to fetch documents');
      }
    } finally {
      setDocumentsLoading(false);
    }
  }, [id, getAuthHeader]);

  const checkNdaStatus = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/nda/check/${id}`, {
        headers: getAuthHeader()
      });
      setNdaAccepted(response.data.accepted);
      if (response.data.accepted) {
        fetchDocuments();
      }
    } catch (error) {
      console.error('Failed to check NDA status');
    }
  }, [id, getAuthHeader, fetchDocuments]);

  useEffect(() => {
    fetchMineral();
  }, [fetchMineral]);

  useEffect(() => {
    if (user && mineral) {
      checkNdaStatus();
    }
  }, [user, mineral, checkNdaStatus]);

  const handleAcceptNda = async () => {
    if (!ndaCheckbox) {
      toast.error('Please check the box to accept the NDA');
      return;
    }

    try {
      await axios.post(`${API_URL}/nda/accept/${id}`, {}, {
        headers: getAuthHeader()
      });
      setNdaAccepted(true);
      setNdaDialogOpen(false);
      toast.success('NDA accepted successfully');
      fetchDocuments();
    } catch (error) {
      toast.error('Failed to accept NDA');
    }
  };

  const handleSubmitOffer = async (e) => {
    e.preventDefault();
    if (!user) {
      toast.error('Please login to make an offer');
      navigate('/auth');
      return;
    }

    if (user.role !== 'buyer') {
      toast.error('Only buyers can make offers');
      return;
    }

    try {
      await axios.post(
        `${API_URL}/offers`,
        {
          mineral_id: id,
          offer_price: parseFloat(offerData.offer_price),
          quantity: parseFloat(offerData.quantity),
          message: offerData.message,
          incoterm: offerData.incoterm
        },
        { headers: getAuthHeader() }
      );
      toast.success(t('detail.offerSent'));
      setOfferDialogOpen(false);
      setOfferData({ offer_price: '', quantity: '', message: '', incoterm: mineral?.incoterm || 'EXW' });
    } catch (error) {
      toast.error(error.response?.data?.detail || t('error.generic'));
    }
  };

  const handleSendMessage = () => {
    if (!user) {
      toast.error('Please login to send a message');
      navigate('/auth');
      return;
    }
    navigate(`/messages?user=${mineral.seller_id}`);
  };

  const getDocumentTypeLabel = (type) => {
    const labels = {
      'certificate_of_origin': 'Certificate of Origin',
      'assay_report': 'Assay Report',
      'mining_license': 'Mining License',
      'export_permit': 'Export Permit',
      'quality_certificate': 'Quality Certificate',
      'environmental_compliance': 'Environmental Compliance',
      'tax_clearance': 'Tax Clearance',
      'company_registration': 'Company Registration',
      'other': 'Other Document'
    };
    return labels[type] || type;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0B1120]">
        <Navbar />
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-slate-900"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0B1120]">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <button
          onClick={() => navigate(-1)}
          className="inline-flex items-center text-[#94A3B8] hover:text-white mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </button>

        <div className="grid lg:grid-cols-2 gap-8">
          {/* Images */}
          <div>
            <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] overflow-hidden aspect-video mb-4">
              {mineral.images && mineral.images[0] ? (
                <img
                  src={mineral.images[0]}
                  alt={mineral.title}
                  className="w-full h-full object-cover"
                  data-testid="mineral-main-image"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-[#1A2440]">
                  <Package className="w-20 h-20 text-[#64748B]" />
                </div>
              )}
            </div>
            {mineral.images && mineral.images.length > 1 && (
              <div className="grid grid-cols-4 gap-2">
                {mineral.images.slice(1).map((img, idx) => (
                  <img
                    key={idx}
                    src={img}
                    alt={`${mineral.title} ${idx + 2}`}
                    className="w-full aspect-square object-cover rounded-xl border border-[#1E293B]"
                  />
                ))}
              </div>
            )}

            {mineral.videos && mineral.videos.length > 0 && (
              <div className="mt-4 space-y-3" data-testid="mineral-videos">
                <p className="text-sm text-[#94A3B8] uppercase tracking-wider font-semibold">
                  Video tour ({mineral.videos.length})
                </p>
                {mineral.videos.map((v, idx) => (
                  <div key={v.path}
                       className="rounded-xl overflow-hidden border border-[#1E293B] bg-[#0B1120]">
                    <video
                      src={`${API_URL}/videos/${v.path}`}
                      controls
                      preload="metadata"
                      playsInline
                      className="w-full max-h-[420px] bg-black"
                      data-testid={`mineral-video-${idx}`}
                    />
                    <p className="px-3 py-2 text-xs text-[#94A3B8] truncate">
                      {v.filename}
                    </p>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Details */}
          <div>
            <div className="bg-[#121A2F] rounded-xl border border-[#1E293B] p-8">
              <span className="inline-block px-3 py-1 text-xs font-medium bg-[#F59E0B]/10 text-[#F59E0B] rounded-xl mb-4">
                {tCategory(mineral.category)}
              </span>
              <h1 className="text-4xl font-bold text-white mb-4" data-testid="mineral-title">
                {mineral.title}
              </h1>

              <div className="flex items-baseline gap-2 mb-6">
                <span className="text-4xl font-bold text-[#F59E0B]" data-testid="mineral-price">
                  ${mineral.price}
                </span>
                <span className="text-xl text-[#94A3B8]">per {mineral.unit}</span>
              </div>

              <div className="space-y-4 mb-8">
                {mineral.grade && (
                  <div className="flex items-center text-[#94A3B8] bg-blue-50 p-3 rounded-xl border border-blue-200">
                    <FileCheck className="w-5 h-5 mr-3 text-blue-600" />
                    <span>Grade: <strong className="text-[#3B82F6]">{mineral.grade}</strong></span>
                  </div>
                )}
                <div className="flex items-center text-[#94A3B8]">
                  <Package className="w-5 h-5 mr-3 text-[#64748B]" />
                  <span>Available: <strong>{mineral.quantity} {mineral.unit}</strong></span>
                </div>
                <div className="flex items-center text-[#94A3B8]">
                  <MapPin className="w-5 h-5 mr-3 text-[#64748B]" />
                  <span>Location: <strong>{mineral.location}</strong></span>
                </div>
                <div className="flex items-center text-[#94A3B8]">
                  <DollarSign className="w-5 h-5 mr-3 text-[#64748B]" />
                  <span>Seller: <strong>{mineral.seller_name}</strong></span>
                </div>
                {/* Incoterm Display */}
                {mineral.incoterm && (
                  <div className="flex items-center text-[#94A3B8] bg-green-50 p-3 rounded-xl border border-green-200">
                    <FileText className="w-5 h-5 mr-3 text-[#10B981]" />
                    <span>
                      Delivery Terms: <strong className="text-[#10B981]">
                        {mineral.incoterm} - {INCOTERMS.find(t => t.code === mineral.incoterm)?.name || mineral.incoterm}
                      </strong>
                    </span>
                  </div>
                )}
              </div>

              {user && user.role === 'buyer' && (
                <div className="space-y-3 mb-8">
                  <Dialog open={offerDialogOpen} onOpenChange={setOfferDialogOpen}>
                    <DialogTrigger asChild>
                      <Button
                        className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white py-6 text-lg font-semibold"
                        data-testid="make-offer-button"
                      >
                        {t('detail.makeOffer')}
                      </Button>
                    </DialogTrigger>
                    <DialogContent data-testid="offer-dialog" className="bg-[#121A2F]">
                      <DialogHeader>
                        <DialogTitle className="text-white">{t('detail.makeOffer')}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSubmitOffer} className="space-y-4 mt-4">
                        <div>
                          <Label htmlFor="offer_price" className="text-white">{t('detail.offerPlaceholder')}</Label>
                          <Input
                            id="offer_price"
                            type="number"
                            step="0.01"
                            data-testid="offer-price-input"
                            value={offerData.offer_price}
                            onChange={(e) => setOfferData({ ...offerData, offer_price: e.target.value })}
                            required
                            placeholder={mineral.price.toString()}
                            className="mt-1 bg-[#121A2F] border-[#1E293B] text-white"
                          />
                        </div>
                        <div>
                          <Label htmlFor="quantity" className="text-white">{t('common.quantity')} ({mineral.unit})</Label>
                          <Input
                            id="quantity"
                            type="number"
                            step="0.01"
                            data-testid="offer-quantity-input"
                            value={offerData.quantity}
                            onChange={(e) => setOfferData({ ...offerData, quantity: e.target.value })}
                            required
                            max={mineral.quantity}
                            placeholder={mineral.quantity.toString()}
                            className="mt-1 bg-[#121A2F] border-[#1E293B] text-white"
                          />
                        </div>
                        <div>
                          <Label htmlFor="message" className="text-white">{t('detail.offerMessage')}</Label>
                          <Textarea
                            id="message"
                            data-testid="offer-message-input"
                            value={offerData.message}
                            onChange={(e) => setOfferData({ ...offerData, message: e.target.value })}
                            rows={3}
                            placeholder="Add a message to the seller..."
                            className="mt-1 bg-[#121A2F] border-[#1E293B] text-white"
                          />
                        </div>
                        <div>
                          <Label htmlFor="incoterm" className="text-white">Preferred Incoterm (Delivery Terms)</Label>
                          <Select
                            value={offerData.incoterm}
                            onValueChange={(value) => setOfferData({ ...offerData, incoterm: value })}
                          >
                            <SelectTrigger className="mt-1 bg-[#121A2F] border-[#1E293B] text-white">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {INCOTERMS.map((term) => (
                                <SelectItem key={term.code} value={term.code}>
                                  {term.code} - {term.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                        <Button
                          type="submit"
                          data-testid="submit-offer-button"
                          className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white"
                        >
                          {t('detail.submitOffer')}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>

                  <Button
                    variant="outline"
                    onClick={handleSendMessage}
                    data-testid="contact-seller-button"
                    className="w-full border-[#1E293B] py-6 text-base"
                  >
                    <MessageSquare className="w-4 h-4 mr-2" />
                    {t('detail.contactSeller')}
                  </Button>
                </div>
              )}

              {!user && (
                <Button
                  onClick={() => navigate('/auth')}
                  data-testid="login-to-offer-button"
                  className="w-full bg-[#F59E0B] hover:bg-[#D97706] text-white py-6 text-lg font-semibold mb-8"
                >
                  {t('auth.loginBtn')} → {t('detail.makeOffer')}
                </Button>
              )}

              <div className="pt-8 border-t border-[#1E293B]">
                <h2 className="text-xl font-bold text-white mb-4">{t('detail.description')}</h2>
                <p className="text-[#94A3B8] leading-relaxed" data-testid="mineral-description">
                  {mineral.description}
                </p>
              </div>

              {Object.keys(mineral.specifications).length > 0 && (
                <div className="pt-8 border-t border-[#1E293B] mt-8">
                  <h2 className="text-xl font-bold text-white mb-4">{t('detail.specs')}</h2>
                  <dl className="space-y-2">
                    {Object.entries(mineral.specifications).map(([key, value]) => (
                      <div key={key} className="flex">
                        <dt className="font-medium text-[#94A3B8] w-1/3">{key}:</dt>
                        <dd className="text-[#94A3B8] w-2/3">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              {/* Terms of Business Section */}
              {mineral.terms_of_business && (
                <div className="pt-8 border-t border-[#1E293B] mt-8">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <FileText className="w-5 h-5 text-[#F59E0B]" />
                    Seller&apos;s Terms of Business
                  </h2>
                  <div className="bg-amber-50 p-4 rounded-xl border border-amber-200">
                    <p className="text-[#94A3B8] whitespace-pre-wrap">{mineral.terms_of_business}</p>
                  </div>
                </div>
              )}

              {/* QOC Documents & NDA Section */}
              {user && mineral.has_qoc_documents && (
                <div className="pt-8 border-t border-[#1E293B] mt-8">
                  <h2 className="text-xl font-bold text-white mb-4 flex items-center gap-2">
                    <Shield className="w-5 h-5 text-blue-600" />
                    Compliance Documents
                  </h2>
                  
                  {!ndaAccepted ? (
                    <div className="bg-[#1A2440] p-6 rounded-xl border border-[#1E293B]">
                      <div className="flex items-start gap-4">
                        <Lock className="w-8 h-8 text-[#64748B] flex-shrink-0 mt-1" />
                        <div>
                          <h3 className="font-semibold text-white mb-2">NDA Required</h3>
                          <p className="text-[#94A3B8] mb-4">
                            To view sensitive compliance documents (Certificate of Origin, Assay Reports, Licenses, etc.), 
                            you must first accept a Non-Disclosure Agreement.
                          </p>
                          <Dialog open={ndaDialogOpen} onOpenChange={setNdaDialogOpen}>
                            <DialogTrigger asChild>
                              <Button className="bg-blue-600 hover:bg-blue-700 text-white">
                                <Shield className="w-4 h-4 mr-2" />
                                Accept NDA to View Documents
                              </Button>
                            </DialogTrigger>
                            <DialogContent className="bg-[#121A2F] max-w-lg">
                              <DialogHeader>
                                <DialogTitle className="text-white flex items-center gap-2">
                                  <Shield className="w-5 h-5 text-blue-600" />
                                  Non-Disclosure Agreement
                                </DialogTitle>
                              </DialogHeader>
                              <div className="mt-4 space-y-4">
                                <div className="bg-[#0B1120] p-4 rounded-xl border border-[#1E293B] max-h-60 overflow-y-auto text-sm text-[#94A3B8]">
                                  <p className="font-semibold mb-2">CONFIDENTIALITY AGREEMENT</p>
                                  <p className="mb-2">
                                    By accepting this Non-Disclosure Agreement (&quot;NDA&quot;), you agree to the following terms:
                                  </p>
                                  <ol className="list-decimal list-inside space-y-2">
                                    <li>You will not disclose, share, or distribute any confidential documents or information obtained from this listing to any third party without explicit written consent from the seller.</li>
                                    <li>The information contained in the compliance documents is proprietary and shall be used solely for the purpose of evaluating this mineral listing for potential purchase.</li>
                                    <li>You acknowledge that unauthorized disclosure may result in legal action and monetary damages.</li>
                                    <li>This agreement shall remain in effect for a period of two (2) years from the date of acceptance.</li>
                                    <li>You agree to comply with all applicable laws and regulations regarding the handling of sensitive commercial information.</li>
                                  </ol>
                                </div>
                                <div className="flex items-center space-x-2">
                                  <Checkbox 
                                    id="nda-checkbox"
                                    checked={ndaCheckbox}
                                    onCheckedChange={setNdaCheckbox}
                                  />
                                  <label htmlFor="nda-checkbox" className="text-sm text-[#94A3B8]">
                                    I have read, understood, and agree to the terms of this Non-Disclosure Agreement
                                  </label>
                                </div>
                                <Button 
                                  onClick={handleAcceptNda}
                                  disabled={!ndaCheckbox}
                                  className="w-full bg-blue-600 hover:bg-blue-700 text-white disabled:opacity-50"
                                >
                                  Accept NDA
                                </Button>
                              </div>
                            </DialogContent>
                          </Dialog>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div>
                      <div className="flex items-center gap-2 text-[#10B981] mb-4 bg-green-50 p-3 rounded-xl border border-green-200">
                        <CheckCircle className="w-5 h-5" />
                        <span className="text-sm font-medium">NDA Accepted - You can view compliance documents</span>
                      </div>
                      
                      {documentsLoading ? (
                        <div className="flex items-center justify-center py-8">
                          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
                        </div>
                      ) : documents.length > 0 ? (
                        <div className="space-y-3">
                          {documents.map((doc) => (
                            <div 
                              key={doc.id} 
                              className={`flex items-center justify-between p-4 rounded-xl border ${
                                doc.is_expired 
                                  ? 'bg-red-50 border-red-200' 
                                  : doc.verified_by_admin 
                                    ? 'bg-green-50 border-green-200'
                                    : 'bg-[#121A2F] border-[#1E293B]'
                              }`}
                            >
                              <div className="flex items-center gap-3">
                                <FileText className={`w-5 h-5 ${doc.is_expired ? 'text-red-500' : 'text-[#64748B]'}`} />
                                <div>
                                  <p className="font-medium text-white">{doc.document_name}</p>
                                  <p className="text-sm text-[#64748B]">{getDocumentTypeLabel(doc.document_type)}</p>
                                  {doc.expiry_date && (
                                    <p className={`text-xs ${doc.is_expired ? 'text-[#EF4444]' : 'text-[#64748B]'}`}>
                                      {doc.is_expired ? (
                                        <span className="flex items-center gap-1">
                                          <AlertTriangle className="w-3 h-3" />
                                          Expired: {new Date(doc.expiry_date).toLocaleDateString()}
                                        </span>
                                      ) : (
                                        <span className="flex items-center gap-1">
                                          <Clock className="w-3 h-3" />
                                          Valid until: {new Date(doc.expiry_date).toLocaleDateString()}
                                        </span>
                                      )}
                                    </p>
                                  )}
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {doc.verified_by_admin && (
                                  <span className="px-2 py-1 bg-[#10B981]/10 text-[#10B981] text-xs font-medium rounded">
                                    Verified
                                  </span>
                                )}
                                {doc.is_expired && (
                                  <span className="px-2 py-1 bg-[#EF4444]/10 text-[#EF4444] text-xs font-medium rounded">
                                    Expired
                                  </span>
                                )}
                                <a 
                                  href={doc.file_url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="text-blue-600 hover:text-[#3B82F6] text-sm font-medium"
                                >
                                  View
                                </a>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <p className="text-[#64748B] text-center py-4">No compliance documents uploaded yet.</p>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}